import { SearchSelect } from '../../components/search-select.js';
import { formatCents } from '@sitzmark/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useReducer, useRef, useState } from 'react';
import { Badge, Button, Callout, Card, Mono, More, Screen, Stat } from '../../components/ui.js';
import { api, errorMessage } from '../../lib/api.js';
import { AddVendorDialog } from '../purchasing/add-vendor.js';
import { CategoryAssign } from './category-assign.js';
import {
  FILE_ACCEPT_ATTRIBUTE,
  formatBytes,
  readFileAsBase64,
  rejectionReason,
} from '../../lib/file.js';
import type { ChosenFile, ImportFieldName, SetupData, WizardState } from './wizard-machine.js';
import {
  FIELD_LABELS,
  PRIMARY_FIELDS,
  SECONDARY_FIELDS,
  commitBlockedReason,
  initialWizardState,
  previewBlockedReason,
  wizardReducer,
} from './wizard-machine.js';
import './wizard.css';

/**
 * The import wizard (SCOPE §5.1) — three screens, one decision each.
 *
 * Drop the file. Confirm what we read. Commit. With a saved template the middle
 * screen is a glance, which is what keeps this inside the ≤5-interaction budget
 * (§2.2) against a job that is currently hundreds of hours of re-keying a year.
 */
export function ImportWizard({
  nav,
  onOpenOrder,
}: {
  nav?: import('react').ReactNode;
  /** "A way in" (item 29): jump to the created order, expanded, ready to send. */
  onOpenOrder?: (purchaseOrderId: string) => void;
} = {}) {
  const [state, dispatch] = useReducer(wizardReducer, initialWizardState);
  const queryClient = useQueryClient();

  const setup = useQuery({
    queryKey: ['import', 'setup'],
    queryFn: () => api.import.setup.query(),
    staleTime: 5 * 60_000,
  });

  const analyse = useMutation({
    mutationFn: (file: ChosenFile) =>
      api.import.analyse.mutate({ file: { filename: file.name, content: file.content } }),
    onSuccess: (analysis) => dispatch({ type: 'analysed', analysis }),
  });

  const preview = useMutation({
    mutationFn: () =>
      api.import.preview.mutate({
        file: { filename: state.file!.name, content: state.file!.content },
        sheetName: state.sheetName!,
        mapping: state.mapping,
        vendorId: state.vendorId!,
        options: { skipZeroQuantity: state.skipZeroQuantity },
      }),
    onSuccess: (result) => dispatch({ type: 'previewed', preview: result }),
  });

  const commit = useMutation({
    mutationFn: () =>
      api.import.commit.mutate({
        file: { filename: state.file!.name, content: state.file!.content },
        sheetName: state.sheetName!,
        mapping: state.mapping,
        vendorId: state.vendorId!,
        defaultDepartmentId: state.departmentId ?? setup.data!.departments[0]!.id,
        defaultCategoryId: state.categoryId,
        // Only the styles filed by hand. Everything else takes the defaults
        // above (§5.1).
        categoryByStyleKey: state.categoryByStyleKey,
        departmentByStyleKey: state.departmentByStyleKey,
        season: state.season,
        options: { skipZeroQuantity: state.skipZeroQuantity },
        saveTemplateAs: state.saveTemplateAs,
      }),
    onSuccess: (result) => {
      dispatch({ type: 'committed', result });
      /*
       * The order this just created has to be in Receiving the moment the
       * operator switches tabs. Without this, the purchase order list was
       * served from cache and the new PO only appeared after enough time had
       * passed or the tab was toggled — which reads as "the import did not
       * work" and gets the file imported a second time.
       */
      void queryClient.invalidateQueries({ queryKey: ['purchasing'] });
      void queryClient.invalidateQueries({ queryKey: ['import', 'setup'] });
    },
  });

  const chooseFile = async (file: File) => {
    const chosen: ChosenFile = {
      name: file.name,
      content: await readFileAsBase64(file),
      sizeBytes: file.size,
    };
    dispatch({ type: 'file-chosen', file: chosen });
    analyse.mutate(chosen);
  };

  if (state.step === 'done' && state.result !== null) {
    return (
      <DoneScreen
        state={state}
        onRestart={() => dispatch({ type: 'restart' })}
        onOpenOrder={onOpenOrder}
      />
    );
  }

  if (state.step === 'preview' && state.preview !== null) {
    return (
      <PreviewScreen
        state={state}
        setup={setup.data ?? null}
        dispatch={dispatch}
        onCommit={() => commit.mutate()}
        committing={commit.isPending}
        error={commit.isError ? errorMessage(commit.error) : null}
      />
    );
  }

  if (state.step === 'confirm' && state.analysis !== null) {
    return (
      <ConfirmScreen
        state={state}
        setup={setup.data ?? null}
        dispatch={dispatch}
        onPreview={() => preview.mutate()}
        previewing={preview.isPending}
        error={preview.isError ? errorMessage(preview.error) : null}
      />
    );
  }

  return (
    <ChooseFileScreen
      nav={nav}
      onChoose={chooseFile}
      busy={analyse.isPending}
      error={analyse.isError ? errorMessage(analyse.error) : null}
      file={state.file}
    />
  );
}

type Dispatch = React.Dispatch<Parameters<typeof wizardReducer>[1]>;

// ---------------------------------------------------------------------------
// Step 1 — drop the file. One focal point, nothing else competing for it.
// ---------------------------------------------------------------------------

function ChooseFileScreen({
  nav,
  onChoose,
  busy,
  error,
  file,
}: {
  nav?: import('react').ReactNode;
  onChoose: (file: File) => void;
  busy: boolean;
  error: string | null;
  file: ChosenFile | null;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [rejected, setRejected] = useState<string | null>(null);

  const accept = (candidate: File | undefined) => {
    if (candidate === undefined) return;
    const reason = rejectionReason(candidate);
    // Caught before a round trip, with the reason on screen (§2.2-7).
    if (reason !== null) {
      setRejected(reason);
      return;
    }
    setRejected(null);
    onChoose(candidate);
  };

  return (
    <Screen
      title="Import a vendor order"
      subtitle="Upload the file the vendor sent. We read it, match it to your catalog, and build the purchase order."
    >
      {nav}
      <div
        className={`dropzone${dragging ? ' dropzone--active' : ''}`}
        onDragOver={(event) => {
          event.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(event) => {
          event.preventDefault();
          setDragging(false);
          accept(event.dataTransfer.files[0]);
        }}
      >
        <p className="dropzone__headline">
          {busy ? `Reading ${file?.name ?? 'the file'}…` : 'Drop the vendor file here'}
        </p>
        <p className="text-secondary">Excel (.xlsx, .xlsb) or CSV, up to 25 MB</p>

        <input
          ref={inputRef}
          type="file"
          accept={FILE_ACCEPT_ATTRIBUTE}
          className="visually-hidden"
          onChange={(event) => accept(event.target.files?.[0])}
        />

        <Button
          variant="primary"
          size="large"
          busy={busy}
          onClick={() => inputRef.current?.click()}
        >
          {busy ? 'Reading…' : 'Choose a file'}
        </Button>
      </div>

      {rejected !== null ? <Callout tone="warning">{rejected}</Callout> : null}
      {error !== null ? (
        <Callout tone="danger" title="That file could not be read">
          {error}
        </Callout>
      ) : null}
    </Screen>
  );
}

// ---------------------------------------------------------------------------
// Step 2 — confirm. With a saved template this is a glance, not a form.
// ---------------------------------------------------------------------------

function ConfirmScreen({
  state,
  setup,
  dispatch,
  onPreview,
  previewing,
  error,
}: {
  state: WizardState;
  setup: SetupData | null;
  dispatch: Dispatch;
  onPreview: () => void;
  previewing: boolean;
  error: string | null;
}) {
  const [addingVendor, setAddingVendor] = useState(false);
  const analysis = state.analysis!;
  const sheet = analysis.sheets.find((entry) => entry.name === state.sheetName) ?? null;
  const orderSheets = analysis.sheets.filter((entry) => entry.recommended);
  const otherSheets = analysis.sheets.filter(
    (entry) => !entry.recommended && entry.productRowCount > 0,
  );
  const blocked = previewBlockedReason(state);

  return (
    <Screen
      title="Check what we read"
      subtitle={state.file?.name}
      context={
        analysis.template.status === 'matched' ? (
          <Badge tone="success">
            {analysis.template.vendorName} template · {analysis.template.confidence}% match
          </Badge>
        ) : null
      }
      destructiveAction={
        <Button variant="ghost" onClick={() => dispatch({ type: 'back' })}>
          Start over
        </Button>
      }
      primaryAction={
        <Button
          variant="primary"
          size="large"
          busy={previewing}
          disabledReason={blocked ?? undefined}
          onClick={onPreview}
        >
          {previewing ? 'Checking your catalog…' : 'Looks right — preview'}
        </Button>
      }
    >
      {analysis.template.status === 'ambiguous' ? (
        <VendorDisambiguation analysis={analysis} state={state} dispatch={dispatch} />
      ) : null}

      {analysis.template.status === 'none' && setup !== null ? (
        <Card title="Which vendor is this order from?">
          <div className="chips">
            {setup.vendors.map((vendor) => (
              <button
                key={vendor.id}
                type="button"
                className={`chip${state.vendorId === vendor.id ? ' chip--on' : ''}`}
                onClick={() => dispatch({ type: 'vendor-chosen', vendorId: vendor.id })}
              >
                {vendor.name}
              </button>
            ))}
            {/*
              The remedy where the gap is found (§2.2-11). You are standing here
              precisely because you are looking for a vendor, and until now a
              new supplier's first file could not be imported at all.
            */}
            <button type="button" className="chip chip--add" onClick={() => setAddingVendor(true)}>
              + Add a vendor
            </button>
          </div>
        </Card>
      ) : null}

      {addingVendor ? (
        <AddVendorDialog
          onClose={() => setAddingVendor(false)}
          onCreated={(vendor) => {
            setAddingVendor(false);
            dispatch({ type: 'vendor-chosen', vendorId: vendor.id });
          }}
        />
      ) : null}

      {orderSheets.length > 1 ? (
        <Card title="Which sheet holds the order?">
          <div className="sheet-cards">
            {orderSheets.map((entry) => (
              <button
                key={entry.name}
                type="button"
                className={`sheet-card${state.sheetName === entry.name ? ' sheet-card--selected' : ''}`}
                onClick={() => dispatch({ type: 'sheet-chosen', sheetName: entry.name })}
              >
                <span className="sheet-card__name">{entry.name}</span>
                <span className="text-secondary num">{entry.productRowCount} products</span>
              </button>
            ))}
          </div>
          {otherSheets.length > 0 ? (
            <p className="text-secondary sheet-cards__note">
              {otherSheets.length} other sheet{otherSheets.length === 1 ? '' : 's'} look like
              reference data rather than an order and were left out.
            </p>
          ) : null}
        </Card>
      ) : null}

      {sheet !== null ? (
        <ColumnConfirmation sheet={sheet} state={state} dispatch={dispatch} />
      ) : null}

      <More label="Import options">
        <label className="toggle">
          <input
            type="checkbox"
            checked={state.skipZeroQuantity}
            onChange={(event) =>
              dispatch({ type: 'skip-zero-quantity-changed', value: event.target.checked })
            }
          />
          <span>
            Skip rows ordering nothing
            <span className="text-secondary"> — vendor forms list the whole catalog</span>
          </span>
        </label>

        <label className="field__label" htmlFor="save-template">
          Remember this layout as
        </label>
        <input
          id="save-template"
          className="input"
          placeholder="e.g. Alpenglow export"
          value={state.saveTemplateAs ?? ''}
          onChange={(event) =>
            dispatch({
              type: 'save-template-changed',
              name: event.target.value === '' ? null : event.target.value,
            })
          }
        />
        <p className="field__hint">Next season&rsquo;s file from this vendor becomes one click.</p>
      </More>

      {error !== null ? (
        <Callout tone="danger" title="Could not read that sheet">
          {error}
        </Callout>
      ) : null}
    </Screen>
  );
}

function VendorDisambiguation({
  analysis,
  state,
  dispatch,
}: {
  analysis: NonNullable<WizardState['analysis']>;
  state: WizardState;
  dispatch: Dispatch;
}) {
  if (analysis.template.status !== 'ambiguous') return null;

  return (
    <Card title="Which vendor is this?">
      {/* Two saved layouts look alike. Guessing could raise a purchase order
          against the wrong supplier, so the wizard asks instead. */}
      <p className="text-secondary confirm__note">
        This file matches more than one saved layout. Pick the vendor it came from.
      </p>
      <div className="sheet-cards">
        {analysis.template.candidates.map((candidate) => (
          <button
            key={candidate.id}
            type="button"
            className={`sheet-card${state.vendorId === candidate.vendorId ? ' sheet-card--selected' : ''}`}
            onClick={() => dispatch({ type: 'vendor-chosen', vendorId: candidate.vendorId })}
          >
            <span className="sheet-card__name">{candidate.vendorName}</span>
            <span className="text-secondary">
              {candidate.name} · {candidate.confidence}%
            </span>
          </button>
        ))}
      </div>
    </Card>
  );
}

/**
 * The heart of the confirm screen.
 *
 * Eight fields, each pre-filled, with three real rows from the operator's own
 * file rendered directly underneath. They verify by looking rather than
 * reasoning — the failure mode we care about ("style name is actually the
 * colour") is obvious on sight and invisible in a dropdown.
 */
function ColumnConfirmation({
  sheet,
  state,
  dispatch,
}: {
  sheet: NonNullable<WizardState['analysis']>['sheets'][number];
  state: WizardState;
  dispatch: Dispatch;
}) {
  const columns = sheet.labels
    .map((label, index) => ({ index, label }))
    .filter((column) => column.label !== '');

  const renderField = (field: ImportFieldName) => {
    const current = state.mapping[field];
    const uncertain = sheet.ambiguousFields.includes(field);

    return (
      <div className="mapping__row" key={field}>
        <label className="mapping__label" htmlFor={`map-${field}`}>
          {FIELD_LABELS[field] ?? field}
          {uncertain ? <Badge tone="warning">check</Badge> : null}
        </label>
        <select
          id={`map-${field}`}
          className="select"
          value={current?.index ?? ''}
          onChange={(event) =>
            dispatch({
              type: 'mapping-changed',
              field,
              column:
                event.target.value === ''
                  ? null
                  : {
                      index: Number(event.target.value),
                      label: sheet.labels[Number(event.target.value)] ?? '',
                    },
            })
          }
        >
          <option value="">Not in this file</option>
          {columns.map((column) => (
            <option key={column.index} value={column.index}>
              {column.label}
            </option>
          ))}
        </select>
        <div className="mapping__samples">
          {sheet.sampleRows.slice(0, 3).map((row, rowIndex) => (
            <span key={rowIndex} className="mapping__sample">
              {current === undefined ? '—' : formatSample((row as unknown[])[current.index])}
            </span>
          ))}
        </div>
      </div>
    );
  };

  return (
    <Card title="Columns we found">
      <div className="mapping">
        <div className="mapping__head">
          <span className="label-micro">Field</span>
          <span className="label-micro">Column</span>
          <span className="label-micro">First three rows in your file</span>
        </div>
        {PRIMARY_FIELDS.map(renderField)}
      </div>

      <More label="Other columns">
        <div className="mapping">{SECONDARY_FIELDS.map(renderField)}</div>
      </More>
    </Card>
  );
}

function formatSample(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  return String(value).slice(0, 28);
}

// ---------------------------------------------------------------------------
// Step 3 — preview. The counts are the focal point.
// ---------------------------------------------------------------------------

function PreviewScreen({
  state,
  setup,
  dispatch,
  onCommit,
  committing,
  error,
}: {
  state: WizardState;
  setup: SetupData | null;
  dispatch: Dispatch;
  onCommit: () => void;
  committing: boolean;
  error: string | null;
}) {
  const preview = state.preview!;
  const blocked = commitBlockedReason(state);
  const needsDepartment = preview.totals.newStyles > 0;

  return (
    <Screen
      title="This is what will happen"
      subtitle={state.file?.name}
      destructiveAction={
        <Button variant="ghost" onClick={() => dispatch({ type: 'back' })}>
          Back
        </Button>
      }
      primaryAction={
        <Button
          variant="primary"
          size="large"
          busy={committing}
          disabledReason={blocked ?? undefined}
          onClick={onCommit}
        >
          {committing ? 'Creating…' : 'Create purchase order'}
        </Button>
      }
    >
      <Card>
        <div className="totals">
          <Stat label="Items on the order" value={preview.totals.rows} emphasis />
          <Stat label="Units" value={preview.totals.totalQuantity} />
          <Stat
            label="New models"
            value={preview.totals.newStyles}
            tone={preview.totals.newStyles > 0 ? 'success' : 'neutral'}
          />
          <Stat label="New variants" value={preview.totals.newVariants} />
          <Stat label="Already in catalog" value={preview.totals.existingVariants} />
        </div>
      </Card>

      {preview.parseStats.zeroQuantityRows > 0 || preview.parseStats.skippedRows > 0 ? (
        <Callout tone="neutral">
          Left out: <strong className="num">{preview.parseStats.zeroQuantityRows}</strong> rows
          ordering nothing and <strong className="num">{preview.parseStats.skippedRows}</strong>{' '}
          heading or section rows.
          {preview.parseStats.duplicateRows > 0 ? (
            <>
              {' '}
              <strong className="num">{preview.parseStats.duplicateRows}</strong> rows repeat a
              barcode already in this file.
            </>
          ) : null}
        </Callout>
      ) : null}

      {needsDepartment && setup !== null ? (
        <Card title={`Where do the ${preview.totals.newStyles} new models go?`}>
          {/* The category tree, read as paths — type to find one (decision 214). */}
          <div className="field">
            <label className="field__label" htmlFor="import-category">
              Category
            </label>
            <SearchSelect
              id="import-category"
              options={setup.departments.map((category) => ({
                value: category.id,
                label: category.path,
              }))}
              value={state.departmentId ?? ''}
              onChange={(value) => {
                if (value !== '') dispatch({ type: 'department-chosen', departmentId: value });
              }}
              placeholder="Pick a category — type to search"
            />
            <p className="field__hint">
              Every new model is filed here unless you file it somewhere else below.
              {setup.categories.length > 0
                ? ' A gender is optional and set below, model by model.'
                : ''}
            </p>
          </div>
        </Card>
      ) : null}

      {/*
        The style list, and where each one gets filed.

        One table rather than two: reviewing what the file contains and deciding
        where it goes are the same read, and splitting them would mean scanning a
        hundred rows twice (§2.2-2). Every style is listed — the old table showed
        the first hundred and said so, which is fine for reading and useless for
        assigning, because the ones you cannot see are the ones that quietly take
        the default.
      */}
      <Card title="By model" padded={false}>
        {state.departmentId === null || setup === null ? (
          <div className="table-scroll">
            <table className="table">
              <thead>
                <tr>
                  <th>Model</th>
                  <th>Model #</th>
                  <th className="num">Variants</th>
                  <th className="num">Units</th>
                  <th>What happens</th>
                </tr>
              </thead>
              <tbody>
                {preview.groups.slice(0, 100).map((group) => (
                  <tr key={group.styleKey}>
                    <td>{group.styleLabel}</td>
                    <td className="code">{group.styleNumber ?? '—'}</td>
                    <td className="num">{group.variantCount}</td>
                    <td className="num">{group.totalQuantity}</td>
                    <td>
                      {group.isNewStyle ? (
                        <Badge tone="success">New model</Badge>
                      ) : group.newVariantCount > 0 ? (
                        <Badge tone="info">{group.newVariantCount} new variants</Badge>
                      ) : (
                        <Badge tone="neutral">Update</Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {preview.groups.length > 100 ? (
              <p className="text-secondary preview__truncated">
                Showing the first 100 of {preview.groups.length} models. All of them will be
                imported.
              </p>
            ) : null}
          </div>
        ) : (
          <CategoryAssign
            groups={preview.groups}
            genders={setup.categories}
            categories={setup.departments}
            defaultDepartmentId={state.departmentId}
            gendered={state.categoryByStyleKey}
            filed={state.departmentByStyleKey}
            onGender={(styleKeys, categoryId) =>
              dispatch({ type: 'styles-assigned', styleKeys, categoryId })
            }
            onFile={(styleKeys, departmentId) =>
              dispatch({ type: 'styles-filed', styleKeys, departmentId })
            }
          />
        )}
      </Card>

      {error !== null ? (
        <Callout tone="danger" title="The import did not go through">
          {error}
        </Callout>
      ) : null}
    </Screen>
  );
}

// ---------------------------------------------------------------------------
// Done — explicit success, never silent (§2.2-6).
// ---------------------------------------------------------------------------

function DoneScreen({
  state,
  onRestart,
  onOpenOrder,
}: {
  state: WizardState;
  onRestart: () => void;
  onOpenOrder?: ((purchaseOrderId: string) => void) | undefined;
}) {
  const result = state.result!;

  return (
    <Screen
      title="Purchase order created"
      secondaryActions={
        <Button variant="ghost" onClick={onRestart}>
          Import another file
        </Button>
      }
      primaryAction={
        // The way in (item 29): the next act is reviewing and sending the
        // order, so that is the primary — importing another file is the rarer
        // path and steps aside.
        onOpenOrder === undefined ? (
          <Button variant="primary" size="large" onClick={onRestart}>
            Import another file
          </Button>
        ) : (
          <Button
            variant="primary"
            size="large"
            onClick={() => onOpenOrder(result.purchaseOrderId)}
          >
            Open {result.purchaseOrderNumber}
          </Button>
        )
      }
    >
      <Callout tone="success" title={`${result.purchaseOrderNumber} is ready to review`}>
        {result.reused
          ? 'This file had been imported before, so the existing purchase order was updated rather than duplicated.'
          : 'It was created as a draft — nothing has been sent to the vendor.'}
      </Callout>

      <Card>
        <div className="totals">
          <Stat label="Purchase order" value={<Mono>{result.purchaseOrderNumber}</Mono>} emphasis />
          <Stat label="Lines" value={result.purchaseOrderLines} />
          <Stat label="Units" value={result.totalUnits} />
          <Stat label="Total cost" value={formatCents(result.totalCostCents)} />
          <Stat label="New products" value={result.createdProducts} />
          <Stat label="New variants" value={result.createdVariants} />
          <Stat label="Updated" value={result.updatedVariants} />
        </div>
      </Card>
    </Screen>
  );
}
